/**
 * Base Hook - abstract class for all hook implementations
 *
 * Provides common functionality:
 * - Hook name and priority management
 * - Logging support
 * - Context access patterns
 */

import type { IHook, IPipelineContext, HookResult } from '../../types';
import { createModuleLogger } from '../../utils';

/**
 * Abstract base class for all hooks
 *
 * Subclasses implement execute() and set name/priority
 *
 * @template TContext - Type of pipeline context
 */
export abstract class BaseHook<TContext extends IPipelineContext = IPipelineContext>
  implements IHook<TContext>
{
  readonly name: string = 'BaseHook';

  readonly priority: number = 100;

  protected readonly logger = createModuleLogger(`Hook[${this.name}]`);

  /**
   * Execute hook logic
   * Subclasses must override
   *
   * @param context - Pipeline context
   * @returns Result indicating pipeline flow
   */
  abstract execute(context: TContext): Promise<HookResult>;

  /**
   * Continue pipeline execution
   * @returns Continue result
   */
  protected continue(): HookResult {
    return { continue: true };
  }

  /**
   * Abort pipeline execution with optional reason
   * @param reason - Why pipeline was aborted
   * @returns Abort result
   */
  protected abort(reason?: string): HookResult {
    if (reason) {
      this.logger.debug(`Aborting: ${reason}`);
    }
    return { continue: false };
  }

  /**
   * Pause pipeline for user input
   * @param _requestId - Request ID
   * @returns Pause result
   */
  protected pause(_requestId: string): HookResult {
    return { continue: false, paused: true };
  }

  /**
   * Return error result
   * @param message - Error message
   * @returns Error result
   */
  protected error(message: string): HookResult {
    this.logger.error(message);
    return { continue: false, error: message };
  }
}
